import React from 'react';
import { Box, Container, Typography, Stack, Link, useTheme, alpha, useMediaQuery } from '@mui/material';
import { useThemeContext } from '../../contexts/ThemeContext';
import { Link as RouterLink } from 'react-router-dom';
import Logo from '../Logo';

const Footer: React.FC = () => {
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        py: { xs: 2, md: 2.5 },
        px: { xs: 2, md: 0 },
        mt: 'auto',
        borderTop: `1px solid ${alpha(darkMode ? '#FFFFFF' : '#000000', 0.1)}`,
        backgroundColor: darkMode ? alpha('#000000', 0.2) : alpha('#f5f5f5', 0.5),
      }}
    >
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 4 }}
          justifyContent="space-between"
          alignItems={{ xs: 'center', md: 'center' }}
          sx={{ textAlign: { xs: 'center', md: 'left' } }}
        >
          {/* Logo and Tagline */}
          <Box sx={{ 
            flex: { xs: '0 0 auto', md: '0 0 auto' }, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            justifyContent: { xs: 'center', md: 'flex-start' }
          }}>
            <Logo variant="small" showText={true} />
            <Typography
              variant="caption"
              sx={{
                color: darkMode ? alpha('#FFFFFF', 0.6) : alpha('#000000', 0.6),
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Making family life easier
            </Typography>
          </Box>

          {/* Quick Links */}
          <Stack
            direction="row"
            spacing={{ xs: 1.5, md: 2 }}
            sx={{
              flex: { xs: '0 0 auto', md: '0 0 auto' },
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: { xs: 1, md: 2 },
            }}
          >
            <Link
              component={RouterLink}
              to="/about"
              sx={{
                color: darkMode ? alpha('#FFFFFF', 0.7) : alpha('#000000', 0.7),
                textDecoration: 'none',
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                '&:hover': {
                  color: '#06B6D4',
                },
              }}
            >
              About
            </Link>
            <Link
              component={RouterLink}
              to="/privacy"
              sx={{
                color: darkMode ? alpha('#FFFFFF', 0.7) : alpha('#000000', 0.7),
                textDecoration: 'none',
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                '&:hover': {
                  color: '#06B6D4',
                },
              }}
            >
              Privacy
            </Link>
            <Link
              component={RouterLink}
              to="/terms"
              sx={{
                color: darkMode ? alpha('#FFFFFF', 0.7) : alpha('#000000', 0.7),
                textDecoration: 'none',
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                '&:hover': {
                  color: '#06B6D4',
                },
              }}
            >
              Terms
            </Link>
            <Link
              href="mailto:support@kivity.app"
              sx={{
                color: darkMode ? alpha('#FFFFFF', 0.7) : alpha('#000000', 0.7),
                textDecoration: 'none',
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                '&:hover': {
                  color: '#06B6D4',
                },
              }}
            >
              Support
            </Link>
          </Stack>

          {/* Copyright */}
          <Box sx={{ 
            flex: { xs: '0 0 auto', md: '0 0 auto' }, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            justifyContent: { xs: 'center', md: 'flex-end' }
          }}>
            <Typography
              variant="caption"
              sx={{
                color: darkMode ? alpha('#FFFFFF', 0.5) : alpha('#000000', 0.5),
                fontSize: { xs: '0.65rem', md: '0.7rem' },
                whiteSpace: 'nowrap',
              }}
            >
              © {new Date().getFullYear()} Kivity v 1.10.9
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer; 
